import type { Mark, Node, Schema } from 'prosemirror-model'
import type { Transaction } from 'prosemirror-state'

import { breakLines } from './break-lines'
import type { Measure } from './measure'

type Range = { from: number; to: number }

type Word = { width: number; firstMarks: ReadonlyArray<Mark>; lastMarks: ReadonlyArray<Mark> }

type Gap = Range & { breaks: number; spaces: number }

type Edit = Range & { node: Node }

const sharedMarks = (a: ReadonlyArray<Mark>, b: ReadonlyArray<Mark>) =>
  a.filter((mark) => b.some((other) => other.eq(mark)))

// Headings and code blocks take no hard break; inserting one would split them
export const allowsBreaks = (block: Node) =>
  block.type.contentMatch.matchType(block.type.schema.nodes.hard_break) != null

// Start of the visual line at `pos`: just after the previous hard break, else the block start
export const lineStart = (doc: Node, pos: number) => {
  const $pos = doc.resolve(pos)
  if (!$pos.parent.isTextblock) return pos
  let start = 0
  $pos.parent.forEach((child, offset) => {
    if (child.type.name === 'hard_break' && offset < $pos.parentOffset) {
      start = offset + child.nodeSize
    }
  })
  return $pos.start() + start
}

// Re-break one segment: every gap becomes a single space or a single hard break
const planSegment = (
  schema: Schema,
  words: Array<Word>,
  gaps: Array<Gap>,
  measure: Measure
): Array<Edit> => {
  const gapWidths = gaps.map((_, i) => measure.text(' ', words[i].lastMarks))
  const breaks = new Set(
    breakLines(
      words.map((word) => word.width),
      gapWidths,
      measure.maxWidth
    )
  )
  return gaps.flatMap((gap, i) => {
    const size = gap.to - gap.from
    // A tag or link running across the gap stays one run
    const marks = sharedMarks(words[i].lastMarks, words[i + 1].firstMarks)
    if (breaks.has(i)) {
      if (gap.breaks === 1 && size === 1) return []
      const breakMarks = marks.filter((mark) => !mark.type.spec.code)
      return [{ ...gap, node: schema.nodes.hard_break.create(null, null, breakMarks) }]
    }
    if (gap.spaces === 1 && size === 1) return []
    return [{ ...gap, node: schema.text(' ', marks) }]
  })
}

// Edits for one textblock from `start` on
const planBlock = (
  block: Node,
  contentStart: number,
  start: number,
  measure: Measure
): Array<Edit> => {
  const schema = block.type.schema
  const edits: Array<Edit> = []
  let words: Array<Word> = []
  let gaps: Array<Gap> = []
  let word: Word | null = null
  let gap: Gap | null = null

  // Leading and trailing gaps of a segment stay as they are
  const closeSegment = () => {
    if (word) words.push(word)
    if (words.length > 1) {
      edits.push(...planSegment(schema, words, gaps.slice(0, words.length - 1), measure))
    }
    words = []
    gaps = []
    word = null
    gap = null
  }
  const addGap = (from: number, to: number, kind: 'breaks' | 'spaces') => {
    if (word) {
      words.push(word)
      word = null
    }
    if (!words.length) return
    if (!gap) {
      gap = { from, to, breaks: 0, spaces: 0 }
      gaps.push(gap)
    }
    gap.to = to
    gap[kind] += kind === 'spaces' ? to - from : 1
  }
  const addPiece = (width: number, marks: ReadonlyArray<Mark>) => {
    // An empty line (stanza gap) is kept and ends the segment
    if (gap && gap.breaks > 1) closeSegment()
    gap = null
    if (word) {
      word.width += width
      word.lastMarks = marks
    } else {
      word = { width, firstMarks: marks, lastMarks: marks }
    }
  }

  block.forEach((child, offset) => {
    const pos = contentStart + offset
    if (pos < start) return
    if (child.isText) {
      for (const match of child.text!.matchAll(/ +|[^ ]+/g)) {
        const from = pos + match.index!
        if (match[0][0] === ' ') addGap(from, from + match[0].length, 'spaces')
        else addPiece(measure.text(match[0], child.marks), child.marks)
      }
    } else if (child.type.name === 'hard_break') {
      addGap(pos, pos + 1, 'breaks')
    } else {
      addPiece(measure.node(pos, child), child.marks)
    }
  })
  closeSegment()
  return edits
}

// Re-break from the line holding `from` through the end of the block holding `to`
// - Earlier lines stay put; every later hard break is recomputed.
export const reflow = (tr: Transaction, from: number, to: number, measure: Measure) => {
  const { doc } = tr
  const start = lineStart(doc, from)
  const edits: Array<Edit> = []
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isTextblock) return true
    if (allowsBreaks(node)) {
      edits.push(...planBlock(node, pos + 1, Math.max(start, pos + 1), measure))
    }
    return false
  })
  // Back to front, so positions computed on `doc` stay valid
  edits
    .sort((a, b) => b.from - a.from)
    .forEach((edit) => tr.replaceWith(edit.from, edit.to, edit.node))
  return tr
}
